import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Zap,
  Plus,
  FolderPlus,
  Sparkles,
  Search,
  MoreHorizontal,
  Play,
  Pause,
  Edit3,
  Copy,
  Trash2,
  Settings,
  BarChart3,
  FileText,
  Activity,
  ChevronRight,
  Mail,
  Bell,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Building2,
  Ticket,
  CreditCard,
  Shield,
  Key,
  Filter,
  ArrowUpRight,
  Eye,
  Send,
  Folder,
  Archive,
  ArrowRightCircle,
  Wand2,
  Loader2,
  Calendar,
  RotateCcw,
  GitBranch,
  ShieldAlert,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import WorkflowLibraryPanel from '../../../components/automations/WorkflowLibraryPanel';
import AutomationStatisticsPanel from '../../../components/automations/AutomationStatisticsPanel';
import WorkflowTemplatesPanel from '../../../components/automations/WorkflowTemplatesPanel';
import WorkflowDeliveryLogsPanel from '../../../components/automations/WorkflowDeliveryLogsPanel';
import WorkflowSettingsPanel from '../../../components/automations/WorkflowSettingsPanel';
import {
  AUTOMATION_TABS,
  normalizeAutomationTab,
} from '../../../utils/automationTabsUtils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Status badge component
const WorkflowStatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
    paused: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Paused' },
    draft: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', label: 'Draft' },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
  };
  const c = config[status] || config.draft;
  return <Badge className={`${c.bg} ${c.text} border-0`}>{c.label}</Badge>;
};

// Category icon component
const CategoryIcon = ({ category }) => {
  const icons = {
    authentication: Key,
    agency_lifecycle: Building2,
    client_self_repair: Users,
    support_tickets: Ticket,
    billing: CreditCard,
    system_alerts: AlertTriangle,
  };
  const Icon = icons[category] || Zap;
  return <Icon className="w-4 h-4" />;
};

// Sender scope badge
const SenderScopeBadge = ({ scope }) => {
  if (scope === 'platform') {
    return (
      <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
        Platform
      </span>
    );
  }
  if (scope === 'agency_with_fallback') {
    return (
      <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
        Agency + Fallback
      </span>
    );
  }
  return (
    <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
      Agency
    </span>
  );
};

// Create workflow menu - matches Agency Automations dropdown
const CreateWorkflowMenu = ({ onClose, onSelect }) => {
  const options = [
    {
      id: 'scratch',
      icon: Plus,
      label: 'Start from Scratch',
      description: 'Build a custom workflow from the ground up',
      color: 'emerald',
    },
    {
      id: 'ai',
      icon: Sparkles,
      label: 'Build with AI',
      description: 'Describe your workflow and let AI create it',
      color: 'purple',
      badge: 'Beta',
    },
    {
      id: 'template',
      icon: FileText,
      label: 'Select from Template',
      description: 'Start with a pre-built workflow template',
      color: 'blue',
    },
    {
      id: 'duplicate',
      icon: Copy,
      label: 'Duplicate Existing Workflow',
      description: 'Copy and modify an existing workflow',
      color: 'amber',
    },
    {
      id: 'platform-wide',
      icon: Building2,
      label: 'Platform-Wide Workflow',
      description: 'Create a system-wide workflow for all agencies',
      color: 'cyan',
    },
  ];

  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    purple: 'bg-purple-500/20 text-purple-400',
    blue: 'bg-blue-500/20 text-blue-400',
    amber: 'bg-amber-500/20 text-amber-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      {/* Menu */}
      <div 
        className="absolute right-0 top-full mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl z-50 overflow-hidden"
        data-testid="create-workflow-menu"
      >
        <div className="p-3 border-b border-zinc-700">
          <h3 className="text-sm font-semibold text-white">Create Workflow</h3>
          <p className="text-xs text-zinc-400 mt-0.5">Choose how to start your workflow</p>
        </div>
        
        <div className="p-2">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => onSelect(option.id)}
              className="group w-full flex items-start gap-3 p-3 rounded-lg hover:bg-zinc-800 transition-colors text-left"
              data-testid={`create-option-${option.id}`}
            >
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                colorClasses[option.color]
              )}>
                <option.icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">{option.label}</span>
                  {option.badge && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-500/20 text-purple-400 rounded">
                      {option.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-0.5">{option.description}</p>
              </div>
              <ChevronRight size={16} className="text-zinc-500 group-hover:text-zinc-400 mt-2.5 flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// Test workflow modal
const TestWorkflowModal = ({ isOpen, onClose, workflow, token, onSuccess }) => {
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSendTest = async () => {
    if (!testEmail || !workflow) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/platform/system-automations/workflows/${workflow.id}/test?recipient_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      if (res.data.success) {
        toast.success('Test email sent successfully');
        onSuccess?.();
      } else {
        toast.error(res.data.error || 'Failed to send test email');
      }
    } catch (err) {
      const error = err.response?.data?.detail || 'Failed to send test email';
      setResult({ success: false, error });
      toast.error(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-500" />
            Test Workflow: {workflow?.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Send a test email to verify this workflow is working correctly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Recipient Email</label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-xs text-zinc-500 mb-1">Trigger Event</p>
            <code className="text-sm text-emerald-400">{workflow?.trigger_event}</code>
          </div>
          {result && (
            <div className={`p-3 rounded-lg ${result.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center gap-2">
                {result.success ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400" />
                )}
                <span className={result.success ? 'text-emerald-400' : 'text-red-400'}>
                  {result.success ? 'Test email sent successfully' : 'Test failed'}
                </span>
              </div>
              {result.error && <p className="text-xs text-red-300 mt-2">{result.error}</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleSendTest}
              disabled={sending || !testEmail}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Workflow Modal
const CreateWorkflowModal = ({ isOpen, onClose, token, onSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'authentication',
    trigger_event: '',
    folder_id: null,
  });
  const [creating, setCreating] = useState(false);

  const CATEGORIES = [
    { value: 'authentication', label: 'Authentication' },
    { value: 'agency_lifecycle', label: 'Agency Lifecycle' },
    { value: 'client_self_repair', label: 'Client / Self-Repair' },
    { value: 'support_tickets', label: 'Support / Tickets' },
    { value: 'billing', label: 'Billing' },
    { value: 'system_alerts', label: 'System Alerts' },
  ];

  const TRIGGERS = [
    { value: 'user.password_reset_requested', label: 'Password Reset Requested' },
    { value: 'user.email_verification_requested', label: 'Email Verification Requested' },
    { value: 'agency.created', label: 'Agency Created' },
    { value: 'agency.admin_invited', label: 'Agency Admin Invited' },
    { value: 'agency.trial_ending', label: 'Agency Trial Ending' },
    { value: 'agency.subscription_failed', label: 'Subscription Payment Failed' },
    { value: 'ticket.created', label: 'Ticket Created' },
    { value: 'ticket.reply_received', label: 'Ticket Reply Received' },
    { value: 'self_repair.signup_created', label: 'Self-Repair Signup Created' },
    { value: 'system.import_failed', label: 'Import Failed' },
    { value: 'system.mailgun_failed', label: 'Mailgun Delivery Failed' },
  ];

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('Workflow name is required');
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/platform/system-automations/workflows`, {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        trigger_event: formData.trigger_event || null,
        sender_scope: 'platform',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Workflow created');
      onClose();
      onSuccess?.();
      
      // Navigate to builder
      const workflowId = res.data.workflow?.id;
      if (workflowId) {
        navigate(`/platform/automations/workflows/${workflowId}/builder`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-500" />
            Create Workflow
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Create a new system workflow. It will start as a draft.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Workflow Name *</label>
            <Input
              placeholder="e.g., Password Reset Email"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Category</label>
            <Select
              value={formData.category}
              onValueChange={(v) => setFormData({ ...formData, category: v })}
            >
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Initial Trigger (optional)</label>
            <Select
              value={formData.trigger_event}
              onValueChange={(v) => setFormData({ ...formData, trigger_event: v })}
            >
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Select trigger or add later in builder" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {TRIGGERS.map((trigger) => (
                  <SelectItem key={trigger.value} value={trigger.value}>{trigger.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Description (optional)</label>
            <Textarea
              placeholder="Describe what this workflow does..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
              rows={2}
            />
          </div>
          
          <div className="p-3 bg-zinc-800/50 rounded-lg flex items-center gap-2">
            <Badge className="bg-zinc-600/50 text-zinc-400 border-0">Draft</Badge>
            <span className="text-xs text-zinc-500">Workflow will be created as draft</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !formData.name}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Workflow
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Folder Modal
const CreateFolderModal = ({ isOpen, onClose, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: null,
    color: '#10b981',
  });
  const [creating, setCreating] = useState(false);

  const COLORS = [
    { value: '#10b981', label: 'Emerald' },
    { value: '#f59e0b', label: 'Amber' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#8b5cf6', label: 'Purple' },
    { value: '#ef4444', label: 'Red' },
    { value: '#ec4899', label: 'Pink' },
  ];

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('Folder name is required');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/platform/automations/folders`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Folder created');
      setFormData({ name: '', category: null, color: '#10b981' });
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-amber-500" />
            Create Folder
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Organize your workflows into folders.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Folder Name *</label>
            <Input
              placeholder="e.g., Onboarding Workflows"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Color</label>
            <div className="flex gap-2">
              {COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  className={cn(
                    'w-8 h-8 rounded-lg transition-all',
                    formData.color === color.value
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-zinc-900'
                      : 'hover:scale-110'
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !formData.name}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}
            Create Folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Build with AI Modal - Recipe Picker + AI Generator
const BuildWithAIModal = ({ isOpen, onClose, token, onSuccess }) => {
  const navigate = useNavigate();
  const [builderMode, setBuilderMode] = useState('recipes');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);

  const [aiPrompt, setAiPrompt] = useState('');
  const [categoryHint, setCategoryHint] = useState('');
  const [triggerHint, setTriggerHint] = useState('');
  const [audienceHint, setAudienceHint] = useState('');
  const [toneHint, setToneHint] = useState('');
  const [complexityHint, setComplexityHint] = useState('');
  const [showAdvancedHints, setShowAdvancedHints] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDraft, setGeneratedDraft] = useState(null);
  const [generationError, setGenerationError] = useState('');
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);

  const QUICK_PROMPT_CHIPS = [
    { label: 'Agency email verification', prompt: 'When a new agency signs up, verify the agency owner email with a secure 6-digit code, handle resend, lockout after 5 failed attempts, and audit logging.' },
    { label: 'Forgot password reset', prompt: 'When a user requests forgot password, lookup across all login sources, send generic response, generate hashed reset token, send reset email, and audit log.' },
    { label: 'Missing document follow-up', prompt: 'When a client has a missing document, send reminders, wait, check upload status, send a second reminder, and create a staff task.' },
    { label: 'Failed payment recovery', prompt: 'When payment failed or card declined, notify the client, send retry reminders, warn about access, and alert billing staff.' },
    { label: 'Dispute status update', prompt: 'When dispute status changes, notify the client, update the portal, wait, send next-step education, and add a staff note.' },
    { label: 'Consultation no-show recovery', prompt: 'When a consultation appointment is a no-show, send a friendly reschedule message, wait, send a second reminder, and assign staff follow-up.' },
    { label: 'PostGrid delivery failure alert', prompt: 'When PostGrid mail delivery fails or letter is returned, alert platform admin, alert agency staff, create retry task, and log audit note.' },
    { label: 'Trial ending reminder', prompt: 'When agency trial is ending soon, send reminder email, wait, send final reminder with upgrade CTA, and alert customer success.' },
    { label: 'Client inactive for 14 days', prompt: 'When a client is inactive with no login for 14 days, send reactivation email, wait, send reminder, and create staff re-engagement task.' },
    { label: 'Credit report imported follow-up', prompt: 'When a credit report is imported, email the client with review instructions and next-step guidance after a short delay.' },
    { label: 'Whop paid member onboarding', prompt: 'When a Whop paid membership is activated, send welcome email, portal access instructions, wait 2 days, and send engagement follow-up.' },
  ];

  const STEP_TYPE_LABELS = {
    trigger: 'Trigger',
    backend_action: 'Backend action',
    send_email: 'Email',
    send_sms: 'SMS',
    delay: 'Wait',
    wait: 'Wait',
    condition: 'Condition',
    branch: 'Branch',
    staff_alert: 'Staff alert',
    send_notification: 'Notification',
    audit_log: 'Audit log',
    audit_note: 'Audit log',
    security_lock: 'Security lock',
    update_record: 'Update record',
    manual_review: 'Manual review',
    create_task: 'Task',
    end: 'End',
  };

  // Professional workflow recipes based on GoHighLevel/ActiveCampaign patterns
  const WORKFLOW_RECIPES = [
    {
      id: 'whop_paid_onboarding',
      name: 'Whop Paid Client Onboarding',
      description: 'Complete onboarding flow for new paid Whop members including portal access, welcome emails, and credit report upload reminders.',
      category: 'whop',
      scope: 'Platform',
      trigger: 'whop.membership_activated',
      icon: CreditCard,
      color: 'emerald',
      expectedSteps: 12,
      audience: 'New paid Whop customers',
    },
    {
      id: 'whop_free_nurture',
      name: 'Whop Free Community Nurture',
      description: 'Nurture free community members with educational content and upgrade invitations.',
      category: 'whop',
      scope: 'Platform',
      trigger: 'whop.free_user_joined',
      icon: Users,
      color: 'blue',
      expectedSteps: 8,
      audience: 'Free community members',
    },
    {
      id: 'failed_payment_recovery',
      name: 'Failed Payment Recovery',
      description: 'Automated payment recovery workflow with reminders, access restrictions, and staff alerts.',
      category: 'billing',
      scope: 'Platform',
      trigger: 'whop.payment_failed',
      icon: AlertTriangle,
      color: 'red',
      expectedSteps: 10,
      audience: 'Customers with failed payments',
    },
    {
      id: 'cancellation_access_lock',
      name: 'Cancellation & Access Lock',
      description: 'Handle membership cancellations with access revocation, winback emails, and staff notifications.',
      category: 'billing',
      scope: 'Platform',
      trigger: 'whop.membership_cancelled',
      icon: XCircle,
      color: 'amber',
      expectedSteps: 8,
      audience: 'Cancelled members',
    },
    {
      id: 'self_repair_onboarding',
      name: 'Self-Repair Client Onboarding',
      description: 'Onboard self-repair clients with portal access, document requests, and progress tracking.',
      category: 'client_self_repair',
      scope: 'Agency + Fallback',
      trigger: 'self_repair.signup_created',
      icon: FileText,
      color: 'teal',
      expectedSteps: 10,
      audience: 'New self-repair clients',
    },
    {
      id: 'agency_client_onboarding',
      name: 'Agency Client Onboarding',
      description: 'Welcome new agency clients with portal invites, onboarding tasks, and follow-up sequences.',
      category: 'agency_lifecycle',
      scope: 'Agency',
      trigger: 'agency.client_created',
      icon: Building2,
      color: 'indigo',
      expectedSteps: 9,
      audience: 'New agency clients',
    },
    {
      id: 'document_request_followup',
      name: 'Document Request Follow-Up',
      description: 'Automated reminders for pending document requests with escalation to staff.',
      category: 'client_self_repair',
      scope: 'Agency + Fallback',
      trigger: 'client.document_requested',
      icon: FileText,
      color: 'purple',
      expectedSteps: 7,
      audience: 'Clients with pending documents',
    },
    {
      id: 'credit_report_reminder',
      name: 'Credit Report Upload Reminder',
      description: 'Remind clients to upload their credit reports with progressive urgency.',
      category: 'client_self_repair',
      scope: 'Agency + Fallback',
      trigger: 'client.portal_invited',
      icon: FileText,
      color: 'cyan',
      expectedSteps: 6,
      audience: 'Clients without credit reports',
    },
    {
      id: 'consultation_noshow',
      name: 'Consultation No-Show Recovery',
      description: 'Re-engage clients who missed consultations with rescheduling options.',
      category: 'client_self_repair',
      scope: 'Agency',
      trigger: 'appointment.no_show',
      icon: Calendar,
      color: 'orange',
      expectedSteps: 5,
      audience: 'No-show consultation clients',
    },
    {
      id: 'dispute_round_update',
      name: 'Dispute Round Update',
      description: 'Notify clients when dispute rounds are processed with next steps guidance.',
      category: 'client_self_repair',
      scope: 'Agency + Fallback',
      trigger: 'client.dispute_status_changed',
      icon: CheckCircle2,
      color: 'green',
      expectedSteps: 5,
      audience: 'Active dispute clients',
    },
    {
      id: 'portal_welcome',
      name: 'Client Portal Welcome',
      description: 'Welcome sequence for new portal users with login instructions and getting started guide.',
      category: 'client_self_repair',
      scope: 'Agency + Fallback',
      trigger: 'client.portal_invited',
      icon: Key,
      color: 'blue',
      expectedSteps: 4,
      audience: 'New portal users',
    },
    {
      id: 'agency_owner_welcome',
      name: 'Agency Owner Welcome',
      description: 'Onboard new agency owners with platform tour, setup tasks, and success tips.',
      category: 'agency_lifecycle',
      scope: 'Platform',
      trigger: 'agency.owner_invited',
      icon: Building2,
      color: 'emerald',
      expectedSteps: 6,
      audience: 'New agency owners',
    },
    {
      id: 'intake_review',
      name: 'Intake Review Workflow',
      description: 'Process unknown Whop purchases requiring manual review and approval.',
      category: 'whop',
      scope: 'Platform',
      trigger: 'whop.unmapped_purchase',
      icon: Eye,
      color: 'amber',
      expectedSteps: 7,
      audience: 'Unmapped Whop purchases',
    },
  ];

  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    teal: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
  };

  const scopeBadgeClass = {
    'Platform': 'bg-purple-500/20 text-purple-400',
    'Agency': 'bg-blue-500/20 text-blue-400',
    'Agency + Fallback': 'bg-emerald-500/20 text-emerald-400',
  };

  const handleGenerateFromRecipe = async (recipe) => {
    setGenerating(true);
    setSelectedRecipe(recipe);
    try {
      const res = await axios.post(`${API}/platform/system-automations/workflows/generate-from-recipe`, {
        recipe_id: recipe.id,
        custom_prompt: customPrompt || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success && res.data.workflow) {
        toast.success(`"${recipe.name}" workflow created!`);
        onSuccess?.();
        navigate(`/platform/automations/workflows/${res.data.workflow.id}/builder`);
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate workflow');
    } finally {
      setGenerating(false);
      setSelectedRecipe(null);
    }
  };

  const handleUsePromptChip = (prompt) => {
    setAiPrompt(prompt);
    setGenerationError('');
  };

  const handleGenerateWorkflowFromPrompt = async () => {
    const trimmed = aiPrompt.trim();
    if (!trimmed) {
      setGenerationError('Describe your workflow first — what should happen, when, and who should be notified.');
      return;
    }
    setGenerationError('');
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/platform/system-automations/generate-from-prompt`, {
        prompt: trimmed,
        category_hint: categoryHint || undefined,
        trigger_hint: triggerHint || undefined,
        audience: audienceHint || undefined,
        tone: toneHint || undefined,
        complexity: complexityHint || undefined,
        scope: 'platform',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success && res.data.draft) {
        setGeneratedDraft(res.data.draft);
      } else {
        setGenerationError('Could not generate workflow draft. Please try again.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setGenerationError(typeof detail === 'string' ? detail : 'Failed to generate workflow draft.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateDraft = () => {
    handleGenerateWorkflowFromPrompt();
  };

  const handleCreateGeneratedDraft = async () => {
    if (!generatedDraft) return;
    setIsCreatingDraft(true);
    setGenerationError('');
    try {
      const res = await axios.post(`${API}/platform/system-automations/workflows`, {
        name: generatedDraft.name,
        description: generatedDraft.description,
        category: generatedDraft.category,
        trigger_event: generatedDraft.trigger_event,
        status: 'draft',
        steps: generatedDraft.steps || [],
        source: generatedDraft.source || 'ai_prompt',
        prompt_source: generatedDraft.prompt_source || aiPrompt.trim(),
        validation: generatedDraft.validation,
        ai_generated: true,
        sender_scope: 'platform',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success && res.data.workflow) {
        toast.success(`"${generatedDraft.name}" draft created!`);
        onSuccess?.();
        navigate(`/platform/automations/workflows/${res.data.workflow.id}/builder`);
        onClose();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setGenerationError(typeof detail === 'string' ? detail : 'Failed to save workflow draft.');
    } finally {
      setIsCreatingDraft(false);
    }
  };

  const extractRecommendedMessages = (steps = []) =>
    steps
      .filter((s) => ['send_email', 'send_sms', 'send_notification', 'staff_alert'].includes(s.type))
      .map((s) => ({
        type: s.type,
        label: s.label,
        audience: s.config?.audience,
        subject: s.config?.subject,
        body: s.config?.body || s.config?.body_preview || s.config?.message,
      }));

  const resetModal = () => {
    setSelectedRecipe(null);
    setCustomPrompt('');
    setShowCustomize(false);
    setBuilderMode('recipes');
    setAiPrompt('');
    setCategoryHint('');
    setTriggerHint('');
    setAudienceHint('');
    setToneHint('');
    setComplexityHint('');
    setShowAdvancedHints(false);
    setIsGenerating(false);
    setGeneratedDraft(null);
    setGenerationError('');
    setIsCreatingDraft(false);
  };

  // Group recipes by category
  const groupedRecipes = WORKFLOW_RECIPES.reduce((acc, recipe) => {
    const cat = recipe.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(recipe);
    return acc;
  }, {});

  const categoryLabels = {
    whop: 'Whop / Membership',
    billing: 'Billing & Recovery',
    client_self_repair: 'Client / Self-Repair',
    agency_lifecycle: 'Agency Lifecycle',
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetModal(); onClose(); } }}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            {builderMode === 'ai' ? 'AI Workflow Architect' : 'AI Workflow Recipe Builder'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {builderMode === 'ai'
              ? 'Describe what should happen, when it should happen, and who should be notified. The AI will build a draft workflow with triggers, steps, delays, conditions, and fallback actions.'
              : 'Select a professional workflow recipe to generate a complete draft. No prompt required.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex border-b border-zinc-700">
          <button
            type="button"
            onClick={() => { setBuilderMode('recipes'); setGeneratedDraft(null); setGenerationError(''); }}
            className={cn(
              'flex-1 py-2.5 text-sm font-medium transition-colors border-b-2',
              builderMode === 'recipes'
                ? 'border-purple-500 text-white'
                : 'border-transparent text-zinc-400 hover:text-zinc-200'
            )}
          >
            Recipe Library
          </button>
          <button
            type="button"
            onClick={() => setBuilderMode('ai')}
            className={cn(
              'flex-1 py-2.5 text-sm font-medium transition-colors border-b-2',
              builderMode === 'ai'
                ? 'border-purple-500 text-white'
                : 'border-transparent text-zinc-400 hover:text-zinc-200'
            )}
          >
            Ask AI
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {builderMode === 'recipes' && (
            <>
              <div className="flex items-center justify-between px-1">
                <button
                  onClick={() => setShowCustomize(!showCustomize)}
                  className="text-sm text-zinc-400 hover:text-white flex items-center gap-2"
                >
                  <Settings size={14} />
                  {showCustomize ? 'Hide customization' : 'Optional: Customize before generating'}
                </button>
              </div>
              
              {showCustomize && (
                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <label className="text-sm text-zinc-400 mb-2 block">Tell AI what to customize (optional)</label>
                  <Textarea
                    placeholder="e.g., Use different email tone, add SMS step, change wait times..."
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                    rows={3}
                  />
                </div>
              )}

              {Object.entries(groupedRecipes).map(([categoryId, recipes]) => (
                <div key={categoryId}>
                  <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-3 px-1">
                    {categoryLabels[categoryId] || categoryId}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {recipes.map((recipe) => {
                      const Icon = recipe.icon;
                      const isGeneratingRecipe = generating && selectedRecipe?.id === recipe.id;
                      
                      return (
                        <div
                          key={recipe.id}
                          className={cn(
                            'p-4 rounded-xl border transition-all cursor-pointer group',
                            'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800'
                          )}
                          onClick={() => !generating && handleGenerateFromRecipe(recipe)}
                          data-testid={`recipe-${recipe.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border',
                              colorClasses[recipe.color]
                            )}>
                              <Icon size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-white truncate">{recipe.name}</h4>
                                <Badge className={cn('text-[10px] px-1.5 py-0', scopeBadgeClass[recipe.scope])}>
                                  {recipe.scope}
                                </Badge>
                              </div>
                              <p className="text-xs text-zinc-400 line-clamp-2 mb-2">{recipe.description}</p>
                              <div className="flex items-center gap-3 text-xs text-zinc-500">
                                <span className="flex items-center gap-1">
                                  <Zap size={12} className="text-emerald-400" />
                                  {recipe.trigger.replace(/[._]/g, ' ')}
                                </span>
                                <span>{recipe.expectedSteps} steps</span>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {isGeneratingRecipe ? (
                                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}

          {builderMode === 'ai' && !generatedDraft && (
            <div className="space-y-5 px-1">
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">Describe your workflow</label>
                <Textarea
                  placeholder="Example: When a new self-repair client signs up, send a welcome email, wait 2 days, check if documents are uploaded, send a reminder if missing, then alert staff after 5 days."
                  value={aiPrompt}
                  onChange={(e) => { setAiPrompt(e.target.value); setGenerationError(''); }}
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
                  rows={5}
                />
              </div>

              <div>
                <p className="text-xs text-zinc-500 mb-2">Quick prompts</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PROMPT_CHIPS.map((chip) => (
                    <button
                      key={chip.label}
                      type="button"
                      onClick={() => handleUsePromptChip(chip.prompt)}
                      className="text-xs px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:border-purple-500/50 hover:text-white transition-colors"
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowAdvancedHints(!showAdvancedHints)}
                className="text-sm text-zinc-400 hover:text-white flex items-center gap-2"
              >
                <Settings size={14} />
                {showAdvancedHints ? 'Hide advanced options' : 'Optional advanced fields'}
              </button>

              {showAdvancedHints && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Category hint</label>
                    <Select value={categoryHint || 'none'} onValueChange={(v) => setCategoryHint(v === 'none' ? '' : v)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-9">
                        <SelectValue placeholder="Any category" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="none">Any category</SelectItem>
                        <SelectItem value="authentication">Authentication</SelectItem>
                        <SelectItem value="client_self_repair">Client / Self-Repair</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="agency_lifecycle">Agency Lifecycle</SelectItem>
                        <SelectItem value="support_tickets">Support</SelectItem>
                        <SelectItem value="system_alerts">System Alerts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Trigger hint</label>
                    <Input
                      value={triggerHint}
                      onChange={(e) => setTriggerHint(e.target.value)}
                      placeholder="e.g. client.document_missing"
                      className="bg-zinc-800 border-zinc-700 text-white h-9"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Audience</label>
                    <Select value={audienceHint || 'none'} onValueChange={(v) => setAudienceHint(v === 'none' ? '' : v)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-9">
                        <SelectValue placeholder="Mixed" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="none">Mixed</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="staff">Agency staff</SelectItem>
                        <SelectItem value="platform">Platform admin</SelectItem>
                        <SelectItem value="mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Workflow tone</label>
                    <Select value={toneHint || 'professional'} onValueChange={setToneHint}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="compliance_safe">Compliance-safe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-zinc-500 mb-1 block">Complexity</label>
                    <Select value={complexityHint || 'standard'} onValueChange={setComplexityHint}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="simple">Simple</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="advanced">Advanced with branches</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {generationError && (
                <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-300">
                  {generationError}
                </div>
              )}

              <Button
                onClick={handleGenerateWorkflowFromPrompt}
                disabled={isGenerating}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Designing workflow...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Workflow Draft
                  </>
                )}
              </Button>
            </div>
          )}

          {builderMode === 'ai' && generatedDraft && (
            <div className="space-y-5 px-1">
              {generatedDraft.fundelite_context_used && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
                  <Sparkles size={14} className="text-emerald-400" />
                  <span className="text-sm text-emerald-300">FundElite-aware draft</span>
                </div>
              )}
              <div className="p-4 rounded-xl border border-zinc-700 bg-zinc-800/50">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <FileText size={16} className="text-purple-400" />
                  Workflow Summary
                </h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-zinc-500">Name</dt><dd className="text-white font-medium">{generatedDraft.name}</dd></div>
                  <div><dt className="text-zinc-500">Category</dt><dd className="text-white">{generatedDraft.category?.replace(/_/g, ' ')}</dd></div>
                  <div className="md:col-span-2"><dt className="text-zinc-500">Description</dt><dd className="text-zinc-300">{generatedDraft.description}</dd></div>
                  <div><dt className="text-zinc-500">Trigger event</dt><dd className="text-emerald-400 font-mono text-xs">{generatedDraft.trigger_event}</dd></div>
                  <div><dt className="text-zinc-500">Status</dt><dd><Badge className="bg-amber-500/20 text-amber-400">Draft</Badge></dd></div>
                  <div><dt className="text-zinc-500">Estimated steps</dt><dd className="text-white">{(generatedDraft.steps || []).filter((s) => s.type !== 'end').length}</dd></div>
                </dl>
              </div>

              <div className="p-4 rounded-xl border border-zinc-700 bg-zinc-800/50">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <GitBranch size={16} className="text-blue-400" />
                  Workflow Map
                </h3>
                <ol className="space-y-2">
                  {(generatedDraft.steps || []).map((step, idx) => (
                    <li key={`${step.type}-${idx}`} className="flex items-start gap-3 text-sm">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-700 text-zinc-300 text-xs flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-400">
                            {STEP_TYPE_LABELS[step.type] || step.type}
                          </Badge>
                          <span className="text-white font-medium truncate">{step.label}</span>
                        </div>
                        {step.config?.duration != null && (
                          <p className="text-xs text-zinc-500 mt-0.5">Wait {step.config.duration} {step.config.unit}</p>
                        )}
                        {step.config?.condition && (
                          <p className="text-xs text-zinc-500 mt-0.5 font-mono">{step.config.condition}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {extractRecommendedMessages(generatedDraft.steps).length > 0 && (
                <div className="p-4 rounded-xl border border-zinc-700 bg-zinc-800/50">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <MessageSquare size={16} className="text-teal-400" />
                    Recommended Messages
                  </h3>
                  <div className="space-y-3">
                    {extractRecommendedMessages(generatedDraft.steps).map((msg, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-zinc-900/60 border border-zinc-700/80">
                        <p className="text-xs text-zinc-500 mb-1">{msg.label}</p>
                        {msg.subject && <p className="text-sm text-white font-medium">{msg.subject}</p>}
                        {msg.body && <p className="text-xs text-zinc-400 mt-1 line-clamp-3">{msg.body}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-4 rounded-xl border border-zinc-700 bg-zinc-800/50">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <ShieldAlert size={16} className="text-amber-400" />
                  Safety & Logic Checks
                </h3>
                {(generatedDraft.validation?.warnings || []).length > 0 ? (
                  <ul className="space-y-2">
                    {generatedDraft.validation.warnings.map((warning, idx) => (
                      <li key={idx} className="text-sm text-amber-200/90 flex items-start gap-2">
                        <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        {warning}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <CheckCircle2 size={14} />
                    No validation warnings detected.
                  </p>
                )}
                {generatedDraft.validation?.recommended_review && (
                  <p className="text-xs text-zinc-500 mt-3">Staff review recommended before activation.</p>
                )}
                {(generatedDraft.validation?.required_backend_actions || []).length > 0 && (
                  <ul className="space-y-1 mt-3">
                    {generatedDraft.validation.required_backend_actions.map((action, idx) => (
                      <li key={idx} className="text-xs text-orange-300 flex items-start gap-2">
                        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                        Backend action required before activation: {action}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {generationError && (
                <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-300">
                  {generationError}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-zinc-700 pt-4 flex-wrap gap-2">
          {builderMode === 'ai' && generatedDraft ? (
            <>
              <Button
                variant="outline"
                onClick={() => { setGeneratedDraft(null); setGenerationError(''); }}
                className="border-zinc-600 text-zinc-300"
                disabled={isCreatingDraft}
              >
                Edit Prompt
              </Button>
              <Button
                variant="outline"
                onClick={handleRegenerateDraft}
                disabled={isGenerating || isCreatingDraft}
                className="border-zinc-600 text-zinc-300"
              >
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Regenerate
              </Button>
              <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300" disabled={isCreatingDraft}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateGeneratedDraft}
                disabled={isCreatingDraft || isGenerating}
                className="bg-white text-zinc-900 hover:bg-zinc-100"
              >
                {isCreatingDraft ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Create Draft Workflow'
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-zinc-500 flex-1">
                Workflows are created as drafts. Review and customize before publishing.
              </p>
              <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
                Cancel
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Create Template Modal
const CreateTemplateModal = ({ isOpen, onClose, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subject: '',
    preview_text: '',
    body_html: '',
    body_text: '',
    status: 'draft',
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!formData.name || !formData.subject) {
      toast.error('Name and subject are required');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/platform/automations/templates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Template created');
      setFormData({
        name: '',
        category: '',
        subject: '',
        preview_text: '',
        body_html: '',
        body_text: '',
        status: 'draft',
      });
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Create Template
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Create a reusable email template for system workflows.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Template Name *</label>
              <Input
                placeholder="e.g., Password Reset Email"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Category</label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="authentication">Authentication</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="notifications">Notifications</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Subject Line *</label>
            <Input
              placeholder="e.g., Reset Your Password"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Preview Text</label>
            <Input
              placeholder="Text shown in email preview..."
              value={formData.preview_text}
              onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm text-zinc-400">Email Body (HTML)</label>
            <Textarea
              placeholder="<p>Hello {{first_name}},</p>..."
              value={formData.body_html}
              onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              rows={6}
            />
          </div>
          
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-xs text-zinc-500 mb-2">Available Variables:</p>
            <div className="flex flex-wrap gap-1">
              {['{{first_name}}', '{{last_name}}', '{{email}}', '{{reset_link}}', '{{platform_name}}'].map((v) => (
                <code key={v} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">{v}</code>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !formData.name || !formData.subject}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Active Workflows</p>
                <p className="text-2xl font-bold text-emerald-400">{stats?.workflows?.active || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Draft Workflows</p>
                <p className="text-2xl font-bold text-zinc-400">{stats?.workflows?.draft || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-zinc-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Emails Sent (24h)</p>
                <p className="text-2xl font-bold text-white">{stats?.delivery_24h?.sent || 0}</p>
              </div>
              <Mail className="w-8 h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Failed (24h)</p>
                <p className="text-2xl font-bold text-red-400">{stats?.delivery_24h?.failed || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Platform Automation Scope</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <h4 className="font-medium text-emerald-400 mb-2">Platform System Automations Handle:</h4>
              <ul className="grid grid-cols-2 gap-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Password Reset Emails</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Email Verification</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Agency Onboarding</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Agency Admin Invites</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Ticket Notifications</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Billing Alerts</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> System Alerts</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Provider Failure Alerts</li>
              </ul>
            </div>
            <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
              <h4 className="font-medium text-zinc-300 mb-2">Agency Automations Handle (Separately):</h4>
              <ul className="grid grid-cols-2 gap-2 text-sm text-zinc-400">
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3" /> Client Onboarding Sequences</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3" /> Dispute Follow-up</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3" /> Document Reminders</li>
                <li className="flex items-center gap-2"><ChevronRight className="w-3 h-3" /> Payment Reminders</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================================================
// UNIFIED AUTOMATIONS TAB - PostGrid, Email, Dispute, CRM, Usage
// ==================================================
const UnifiedAutomationsTab = ({ token }) => {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [triggerDefinitions, setTriggerDefinitions] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Unified trigger categories
  const UNIFIED_CATEGORIES = [
    { id: 'all', name: 'All Automations', icon: Zap },
    { id: 'postgrid', name: 'PostGrid / Mail', icon: Mail },
    { id: 'email', name: 'Email Events', icon: Send },
    { id: 'dispute', name: 'Disputes', icon: FileText },
    { id: 'crm', name: 'CRM / Contacts', icon: Users },
    { id: 'usage', name: 'Usage / Billing', icon: CreditCard },
  ];

  const fetchAutomations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        params.set('trigger_category', selectedCategory);
      }
      if (searchQuery) {
        params.set('search', searchQuery);
      }
      const res = await axios.get(`${API}/platform/automations/platform?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutomations(res.data.automations || []);
    } catch (err) {
      console.error('Failed to fetch unified automations:', err);
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory, searchQuery]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/automations/templates?source=unified`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, [token]);

  const fetchTriggerDefinitions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/definitions/triggers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTriggerDefinitions(res.data.triggers || {});
    } catch (err) {
      console.error('Failed to fetch trigger definitions:', err);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/automations/stats/platform`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchAutomations();
      fetchTemplates();
      fetchTriggerDefinitions();
      fetchStats();
    }
  }, [fetchAutomations, fetchTemplates, fetchTriggerDefinitions, fetchStats, token]);

  const handleSeedAutomations = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/platform/automations/unified/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Seeded automation templates');
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to seed automations');
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateFromTemplate = async (templateId) => {
    try {
      const res = await axios.post(
        `${API}/platform/automations/unified/from-template/${templateId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Automation created from template');
      fetchAutomations();
      // Navigate to builder
      navigate(`/platform/automations/workflows/${res.data.id}/builder`);
    } catch (err) {
      toast.error('Failed to create automation');
    }
  };

  const handleToggleAutomation = async (automationId) => {
    try {
      await axios.post(
        `${API}/platform/automations/platform/${automationId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Automation status updated');
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to toggle automation');
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    if (!window.confirm('Are you sure you want to delete this automation?')) return;
    try {
      await axios.delete(
        `${API}/platform/automations/platform/${automationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Automation deleted');
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to delete automation');
    }
  };

  const handleDuplicateAutomation = async (automationId) => {
    try {
      const res = await axios.post(
        `${API}/platform/automations/duplicate/${automationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Automation duplicated');
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to duplicate automation');
    }
  };

  const handleArchiveAutomation = async (automationId) => {
    try {
      // Archive is effectively setting status to 'archived' or deleting - using pause for now
      await axios.post(
        `${API}/platform/automations/platform/${automationId}/toggle`,
        { status: 'archived' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Automation archived');
      fetchAutomations();
    } catch (err) {
      toast.error('Failed to archive automation');
    }
  };

  const handleTestAutomation = async (automationId) => {
    try {
      // Find the automation to get its trigger event
      const automation = automations.find(a => a.id === automationId);
      if (!automation?.trigger?.event) {
        toast.error('No trigger configured for this automation');
        return;
      }
      
      const res = await axios.post(
        `${API}/platform/automations/test`,
        {
          automation_id: automationId,
          payload: {
            postgrid: { object_id: 'test_123', status: 'completed' },
            user: { email: 'test@example.com', first_name: 'Test' },
          },
          is_test: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success(`Test run completed: ${res.data.actions_executed || 0} actions executed`);
      } else {
        toast.error(res.data.error || 'Test failed');
      }
    } catch (err) {
      toast.error('Failed to run test');
    }
  };

  const filteredAutomations = automations.filter(a => {
    if (searchQuery) {
      return a.name?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats Banner */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase">Total Automations</p>
              <p className="text-2xl font-bold text-white">{stats.total_automations}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase">Active</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase">Runs (24h)</p>
              <p className="text-2xl font-bold text-blue-400">{stats.runs_24h}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase">Success (24h)</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.success_24h}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar - Categories */}
        <div className="w-56 flex-shrink-0 space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-semibold text-zinc-500 uppercase">Categories</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <nav className="space-y-1">
                {UNIFIED_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const count = cat.id === 'all' 
                    ? automations.length 
                    : automations.filter(a => a.trigger?.category === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                        selectedCategory === cat.id
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span>{cat.name}</span>
                      </div>
                      <span className="text-xs text-zinc-500">{count}</span>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>

          {/* Templates Card */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-semibold text-zinc-500 uppercase">Quick Start</CardTitle>
            </CardHeader>
            <CardContent className="p-2 space-y-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
                onClick={handleSeedAutomations}
                disabled={seeding}
              >
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Seed Templates
              </Button>
              <div className="pt-2 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 px-2 mb-2">Create from Template:</p>
                {templates.slice(0, 3).map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => handleCreateFromTemplate(tpl.id)}
                    className="w-full text-left px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
                  >
                    {tpl.name}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-4">
          {/* Search & Actions */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search automations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-900/50 border-zinc-800 text-white"
              />
            </div>
            <Button 
              variant="outline" 
              className="border-zinc-700 text-zinc-300" 
              onClick={fetchAutomations}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" />
              Create Automation
            </Button>
          </div>

          {/* Automations Table */}
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : filteredAutomations.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-12 text-center">
                <Zap className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No unified automations yet</p>
                <p className="text-sm text-zinc-500 mt-1 mb-4">
                  Create automations for PostGrid, Email, Disputes, CRM, and Usage events
                </p>
                <Button onClick={handleSeedAutomations} disabled={seeding}>
                  {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Seed Default Templates
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-lg border border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader className="bg-zinc-900/80">
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Automation</TableHead>
                    <TableHead className="text-zinc-400">Trigger</TableHead>
                    <TableHead className="text-zinc-400">Steps</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAutomations.map((automation) => (
                    <TableRow 
                      key={automation.id}
                      className="border-zinc-800 hover:bg-zinc-800/50"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            automation.status === 'active' ? 'bg-emerald-500/20' :
                            automation.status === 'draft' ? 'bg-zinc-600/30' : 'bg-amber-500/20'
                          }`}>
                            <Zap className={`w-4 h-4 ${
                              automation.status === 'active' ? 'text-emerald-400' :
                              automation.status === 'draft' ? 'text-zinc-400' : 'text-amber-400'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-white">{automation.name}</p>
                            <p className="text-xs text-zinc-500">{automation.description}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs text-zinc-400 bg-zinc-800 px-2 py-1 rounded">
                          {automation.trigger?.event || 'Not set'}
                        </code>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-300">{automation.steps?.length || 0} steps</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          'text-xs capitalize',
                          automation.status === 'active' && 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                          automation.status === 'paused' && 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                          automation.status === 'draft' && 'bg-zinc-600/30 text-zinc-400 border-zinc-600'
                        )}>
                          {automation.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-zinc-400">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700 w-48">
                            <DropdownMenuItem
                              onClick={() => navigate(`/platform/automations/workflows/${automation.id}/builder`)}
                              className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Open Builder
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleTestAutomation(automation.id)}
                              className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Send Test
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-zinc-700" />
                            <DropdownMenuItem
                              onClick={() => handleToggleAutomation(automation.id)}
                              className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                            >
                              {automation.status === 'active' ? (
                                <>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Pause
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDuplicateAutomation(automation.id)}
                              className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleArchiveAutomation(automation.id)}
                              className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                            >
                              <Archive className="w-4 h-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-zinc-700" />
                            <DropdownMenuItem
                              onClick={() => handleDeleteAutomation(automation.id)}
                              className="text-red-400 focus:bg-red-500/10 focus:text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Templates Tab Component
const TemplatesTab = ({ token, onCreateTemplate }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/automations/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">
          Reusable email templates for system workflows.
        </p>
        <Button
          onClick={onCreateTemplate}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No templates created yet</p>
            <p className="text-sm text-zinc-500 mt-1">Create templates to use in system workflows</p>
            <Button
              onClick={onCreateTemplate}
              className="bg-emerald-500 hover:bg-emerald-600 text-white mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white text-base">{template.name}</CardTitle>
                    <p className="text-sm text-zinc-400">{template.category || 'General'}</p>
                  </div>
                  <WorkflowStatusBadge status={template.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Subject</p>
                  <p className="text-sm text-zinc-300 truncate">{template.subject}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300">
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300">
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Delivery Logs Tab Component
const DeliveryLogsTab = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/platform/system-automations/delivery-logs?limit=50`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const DeliveryStatusBadge = ({ status }) => {
    const config = {
      sent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Sent' },
      delivered: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Delivered' },
      failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
      pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
      bounced: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Bounced' },
    };
    const c = config[status] || config.pending;
    return <Badge className={`${c.bg} ${c.text} border-0`}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchLogs} className="border-zinc-700 text-zinc-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {logs.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No delivery logs yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <Table>
            <TableHeader className="bg-zinc-900/80">
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Timestamp</TableHead>
                <TableHead className="text-zinc-400">Recipient</TableHead>
                <TableHead className="text-zinc-400">Type</TableHead>
                <TableHead className="text-zinc-400">Provider</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400">Message ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell className="text-zinc-400 text-sm">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="text-white">{log.recipient_email}</TableCell>
                  <TableCell className="text-sm text-zinc-300">{log.email_type?.replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                      {log.provider_used?.replace(/_/g, ' ')}
                    </span>
                  </TableCell>
                  <TableCell><DeliveryStatusBadge status={log.status} /></TableCell>
                  <TableCell>
                    {log.mailgun_message_id ? (
                      <code className="text-xs text-zinc-500 truncate max-w-[150px] block">
                        {log.mailgun_message_id}
                      </code>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    enable_system_automations: true,
    enable_password_reset_emails: true,
    enable_email_confirmation_emails: true,
    enable_agency_onboarding_emails: true,
    enable_ticket_notification_emails: true,
    enable_fallback_sender_for_agency: true,
    default_retry_attempts: 3,
    token_expiration_minutes: 60,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/platform/system-automations/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform/system-automations/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const ToggleSetting = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
      <div>
        <label className="text-white font-medium">{label}</label>
        <p className="text-xs text-zinc-400">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`w-12 h-6 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-zinc-600'}`}
      >
        <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${checked ? 'translate-x-6' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Global Automation Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleSetting
            label="Enable System Automations"
            description="Master switch for all platform email automations"
            checked={settings.enable_system_automations}
            onChange={(v) => setSettings({ ...settings, enable_system_automations: v })}
          />
          <ToggleSetting
            label="Password Reset Emails"
            description="Send password reset emails when requested"
            checked={settings.enable_password_reset_emails}
            onChange={(v) => setSettings({ ...settings, enable_password_reset_emails: v })}
          />
          <ToggleSetting
            label="Email Confirmation Emails"
            description="Send email verification links"
            checked={settings.enable_email_confirmation_emails}
            onChange={(v) => setSettings({ ...settings, enable_email_confirmation_emails: v })}
          />
          <ToggleSetting
            label="Agency Onboarding Emails"
            description="Send welcome and invite emails for agencies"
            checked={settings.enable_agency_onboarding_emails}
            onChange={(v) => setSettings({ ...settings, enable_agency_onboarding_emails: v })}
          />
          <ToggleSetting
            label="Ticket Notification Emails"
            description="Send ticket created, replied, and status emails"
            checked={settings.enable_ticket_notification_emails}
            onChange={(v) => setSettings({ ...settings, enable_ticket_notification_emails: v })}
          />
          <ToggleSetting
            label="Platform Fallback for Agency Emails"
            description="Use Platform Mailgun when Agency Mailgun unavailable"
            checked={settings.enable_fallback_sender_for_agency}
            onChange={(v) => setSettings({ ...settings, enable_fallback_sender_for_agency: v })}
          />

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Default Retry Attempts</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={settings.default_retry_attempts}
                onChange={(e) => setSettings({ ...settings, default_retry_attempts: parseInt(e.target.value) || 3 })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Token Expiration (minutes)</label>
              <Input
                type="number"
                min="15"
                max="1440"
                value={settings.token_expiration_minutes}
                onChange={(e) => setSettings({ ...settings, token_expiration_minutes: parseInt(e.target.value) || 60 })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

// Main Page Component
const PlatformAutomationsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();
  
  const [workflows, setWorkflows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Modal states
  const [showCreateWorkflowModal, setShowCreateWorkflowModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showBuildWithAIModal, setShowBuildWithAIModal] = useState(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);

  const rawTab = searchParams.get('tab');
  const activeTab = normalizeAutomationTab(rawTab);

  useEffect(() => {
    if (rawTab !== null && rawTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [rawTab, activeTab, setSearchParams]);

  const fetchWorkflows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/system-automations/workflows?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorkflows(res.data.workflows || []);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      setWorkflows([]);
    }
  }, [token]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/automations/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(res.data.folders || []);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/system-automations/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setStats(null);
      toast.error('Failed to load automation stats');
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchWorkflows();
      fetchFolders();
      fetchStats();
    }
  }, [token, fetchWorkflows, fetchFolders, fetchStats]);

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const handleCreateWorkflow = async (option) => {
    setShowCreateMenu(false);
    if (option === 'scratch' || option === 'platform-wide') {
      // Create workflow draft directly and navigate to builder (no modal)
      try {
        const res = await axios.post(`${API}/platform/system-automations/workflows`, {
          name: 'Untitled Workflow',
          description: '',
          category: 'authentication',
          trigger_event: null,
          sender_scope: option === 'platform-wide' ? 'platform' : 'platform',
          status: 'draft',
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const workflowId = res.data.workflow?.id;
        if (workflowId) {
          navigate(`/platform/automations/workflows/${workflowId}/builder`);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to create workflow');
      }
    } else if (option === 'ai') {
      setShowBuildWithAIModal(true);
    } else if (option === 'template') {
      setShowBuildWithAIModal(true);
    } else if (option === 'duplicate') {
      // Show duplicate workflow selector modal
      toast.info('Duplicate workflow feature coming soon');
    }
  };

  const handleSeedWorkflows = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/platform/system-automations/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchWorkflows();
      fetchStats();
    } catch (err) {
      toast.error('Failed to seed workflows');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-full text-white p-6 lg:p-8 space-y-6" data-testid="platform-automations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-500/20 rounded-xl">
            <Zap className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Automations</h1>
            <p className="text-sm text-zinc-400">
              Build and manage system-wide automated workflows
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {workflows.length === 0 && (
            <Button
              onClick={handleSeedWorkflows}
              disabled={seeding}
              variant="outline"
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            >
              {seeding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Seed Default Workflows
            </Button>
          )}
          <Button 
            variant="outline" 
            className="border-zinc-700 text-zinc-300 gap-2"
            onClick={() => setShowCreateFolderModal(true)}
          >
            <FolderPlus className="w-4 h-4" />
            Create Folder
          </Button>
          <Button 
            variant="outline" 
            className="border-zinc-700 text-zinc-300 gap-2"
            onClick={() => setShowBuildWithAIModal(true)}
          >
            <Sparkles className="w-4 h-4" />
            Build with AI
          </Button>
          <div className="relative">
            <Button
              onClick={() => setShowCreateMenu(!showCreateMenu)}
              className="gap-2 bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-workflow-btn"
            >
              <Plus className="w-4 h-4" />
              Create Workflow
            </Button>
            {showCreateMenu && (
              <CreateWorkflowMenu
                onClose={() => setShowCreateMenu(false)}
                onSelect={handleCreateWorkflow}
              />
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1" data-testid="automation-top-nav">
          {AUTOMATION_TABS.map(({ id, label }) => {
            const TabIcon = {
              workflows: Zap,
              statistics: BarChart3,
              templates: FileText,
              'delivery-logs': Activity,
              settings: Settings,
            }[id] || Zap;

            return (
              <TabsTrigger
                key={id}
                value={id}
                data-testid={`automation-tab-${id}`}
                className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
              >
                <TabIcon className="w-4 h-4 mr-2" />
                {label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="workflows">
          <WorkflowLibraryPanel
            token={token}
            onStatsChange={fetchStats}
            onBuildWithAI={() => setShowBuildWithAIModal(true)}
            onCreateTemplate={() => setShowCreateTemplateModal(true)}
          />
        </TabsContent>

        <TabsContent value="statistics">
          <AutomationStatisticsPanel stats={stats} />
        </TabsContent>

        <TabsContent value="templates">
          <WorkflowTemplatesPanel
            token={token}
            onCreateTemplate={() => setShowCreateTemplateModal(true)}
          />
        </TabsContent>

        <TabsContent value="delivery-logs">
          <WorkflowDeliveryLogsPanel token={token} />
        </TabsContent>

        <TabsContent value="settings">
          <WorkflowSettingsPanel token={token} />
        </TabsContent>
      </Tabs>

      {/* Create Workflow Modal */}
      <CreateWorkflowModal
        isOpen={showCreateWorkflowModal}
        onClose={() => setShowCreateWorkflowModal(false)}
        token={token}
        onSuccess={() => {
          fetchWorkflows();
          fetchStats();
        }}
      />

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        token={token}
        onSuccess={() => {
          fetchFolders();
        }}
      />

      {/* Build with AI Modal */}
      <BuildWithAIModal
        isOpen={showBuildWithAIModal}
        onClose={() => setShowBuildWithAIModal(false)}
        token={token}
        onSuccess={() => {
          fetchWorkflows();
          fetchStats();
        }}
      />

      {/* Create Template Modal */}
      <CreateTemplateModal
        isOpen={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        token={token}
        onSuccess={() => {
          // Refresh templates if needed
        }}
      />

    </div>
  );
};

export default PlatformAutomationsPage;
